/**
 * API Service for Matchday Planning Entities
 * Provides access to related entities for the planning form
 */

import { getToken } from '../utils/auth';
import { LocalMatchdayInfo, MatchInfo, ClubInfo, TransportationInfo, TravelerInfo, PartnerInfo, Vehicle, PartnerDriver } from '../types/matchdayPlanning';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE as string | undefined && /^https?:\/\//i.test((import.meta as any).env?.VITE_API_BASE as string) 
  ? (import.meta as any).env?.VITE_API_BASE as string 
  : window.location.origin;

/**
 * Helper function to make authenticated API requests
 */
async function fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error: ${response.status} - ${errorText}`);
  }

  return response.json();
}

/**
 * Get all local matchdays for selection
 */
export async function getLocalMatchdays(): Promise<LocalMatchdayInfo[]> {
  return fetchWithAuth<LocalMatchdayInfo[]>('/api/MatchdayPlanning/entities/local-matchdays');
}

/**
 * Get all matches for selection
 */
export async function getMatches(): Promise<MatchInfo[]> {
  return fetchWithAuth<MatchInfo[]>('/api/MatchdayPlanning/entities/matches');
}

/**
 * Get all clubs for selection
 */
export async function getClubs(): Promise<ClubInfo[]> {
  return fetchWithAuth<ClubInfo[]>('/api/MatchdayPlanning/entities/clubs');
}

/**
 * Get all transportations for selection
 */
export async function getTransportations(): Promise<TransportationInfo[]> {
  return fetchWithAuth<TransportationInfo[]>('/api/MatchdayPlanning/entities/transportations');
}

/**
 * Get all drivers for selection
 */
export async function getDrivers(): Promise<TravelerInfo[]> {
  return fetchWithAuth<TravelerInfo[]>('/api/MatchdayPlanning/entities/drivers');
}

/**
 * Get all staff members (Staff + Coach) for selection
 */
export async function getStaffMembers(): Promise<TravelerInfo[]> {
  return fetchWithAuth<TravelerInfo[]>('/api/MatchdayPlanning/entities/staff-members');
}

/**
 * Get all partners for selection
 */
export async function getPartners(): Promise<PartnerInfo[]> {
  return fetchWithAuth<PartnerInfo[]>('/api/MatchdayPlanning/entities/partners');
}

/**
 * Get all vehicles for selection
 * @param partnerId Optional filter for partner-specific vehicles
 */
export async function getVehicles(partnerId?: number): Promise<Vehicle[]> {
  const url = partnerId
    ? `/api/MatchdayPlanning/entities/vehicles?partnerId=${partnerId}`
    : '/api/MatchdayPlanning/entities/vehicles';
  return fetchWithAuth<Vehicle[]>(url);
}

/**
 * Get all partner drivers for selection
 * @param partnerId Optional filter for partner-specific drivers
 */
export async function getPartnerDrivers(partnerId?: number): Promise<PartnerDriver[]> {
  const url = partnerId
    ? `/api/MatchdayPlanning/entities/partner-drivers?partnerId=${partnerId}`
    : '/api/MatchdayPlanning/entities/partner-drivers';
  return fetchWithAuth<PartnerDriver[]>(url);
}

/**
 * Combined API object for easier imports
 */
export const matchdayPlanningEntitiesApi = {
  getLocalMatchdays,
  getMatches,
  getClubs,
  getTransportations,
  getDrivers,
  getStaffMembers,
  getPartners,
  getVehicles,
  getPartnerDrivers,
};

export default matchdayPlanningEntitiesApi;

